/**
 * Macierz RBAC (§5.3) — uprawnienia w formacie „Moduł : Akcja" (dokładnie ten zapis,
 * ze spacjami wokół dwukropka — tak prezentuje je dziennik audytu w makiecie).
 * Egzekwowanie: guard Fastify na trasach + maskowanie pól; zakres (tenant/zespół/self)
 * zawęża warstwa API — macierz odpowiada wyłącznie na pytanie „czy rola ma uprawnienie".
 */

import { isRole, type Role } from './roles'

export const PERMISSIONS = [
  // Kadry — akta pracownicze i proces zatrudnienia
  'Kadry : Podgląd',
  'Kadry : Edycja',
  'Kadry : Dane wrażliwe',
  'Kadry : Zapraszanie',
  // Wnioski — o zmianę danych + wnioski portalowe
  'Wnioski : Składanie',
  'Wnioski : Decyzja',
  // Dokumenty i szablony
  'Dokumenty : Podgląd',
  'Dokumenty : Edycja',
  // Urlopy i absencje
  'Urlopy : Podgląd',
  'Urlopy : Wnioskowanie',
  'Urlopy : Akceptacja',
  // Badania lekarskie i BHP
  'Badania : Podgląd',
  'Badania : Edycja',
  // Czas pracy
  'Czas pracy : Podgląd',
  'Czas pracy : Rejestracja',
  'Czas pracy : Akceptacja',
  'Czas pracy : Zamykanie okresów',
  // Płace, deklaracje, raporty
  'Płace : Podgląd',
  'Płace : Zatwierdzanie',
  'Automat : Deklaracje',
  'Raporty : Podgląd',
  // Zespół (zakres managera: podwładni po manager_id)
  'Zespół : Podgląd',
  // Administracja tenantem
  'Konfiguracja : Zarządzanie',
  'Role : Zarządzanie',
  'Audyt : Podgląd',
  // Portal pracownika /me
  'Portal : Własne dane',
] as const

export type Permission = (typeof PERMISSIONS)[number]

export function isPermission(value: unknown): value is Permission {
  return typeof value === 'string' && (PERMISSIONS as readonly string[]).includes(value)
}

/**
 * Macierz rola → uprawnienia wg semantyki §5.3:
 * - hr_admin: pełny moduł HR (akta z odsłanianiem pól wrażliwych, proces zatrudnienia,
 *   wnioski, dokumenty, urlopy, badania, konfiguracja, role/join-requests, audyt),
 * - payroll: listy płac, deklaracje, czas pracy, raporty; wgląd w akta bez edycji
 *   (pola wrażliwe spoza płac maskowane na poziomie pól),
 * - manager: wyłącznie własny zespół — lista + profil ograniczony, akceptacja urlopów
 *   i wpisów czasu pracy zespołu,
 * - employee: portal /me — wyłącznie własne dane.
 */
export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  hr_admin: [
    'Kadry : Podgląd',
    'Kadry : Edycja',
    'Kadry : Dane wrażliwe',
    'Kadry : Zapraszanie',
    'Wnioski : Decyzja',
    'Dokumenty : Podgląd',
    'Dokumenty : Edycja',
    'Urlopy : Podgląd',
    'Urlopy : Akceptacja',
    'Badania : Podgląd',
    'Badania : Edycja',
    /*
     * Podgląd modułów płacowo-czasowych: specjalistka HR otwiera te ekrany
     * (§5.3 — nawigacja filtrowana tak, że to payroll i manager widzą MNIEJ).
     * Decyzje pozostają przy roli payroll: brak „Płace : Zatwierdzanie",
     * „Automat : Deklaracje" i „Czas pracy : Zamykanie okresów".
     */
    'Czas pracy : Podgląd',
    'Płace : Podgląd',
    'Raporty : Podgląd',
    'Konfiguracja : Zarządzanie',
    'Role : Zarządzanie',
    'Audyt : Podgląd',
  ],
  payroll: [
    'Kadry : Podgląd',
    'Czas pracy : Podgląd',
    'Czas pracy : Zamykanie okresów',
    'Płace : Podgląd',
    'Płace : Zatwierdzanie',
    'Automat : Deklaracje',
    'Raporty : Podgląd',
  ],
  manager: [
    'Zespół : Podgląd',
    'Urlopy : Akceptacja',
    'Czas pracy : Akceptacja',
  ],
  employee: [
    'Portal : Własne dane',
    'Wnioski : Składanie',
    'Urlopy : Wnioskowanie',
    'Czas pracy : Rejestracja',
  ],
}

/** Czy którakolwiek z ról użytkownika nadaje dane uprawnienie. */
export function hasPermission(roles: readonly Role[], perm: Permission): boolean {
  return roles.some((role) => ROLE_PERMISSIONS[role].includes(perm))
}

/** Suma uprawnień wynikająca z zestawu ról (bez duplikatów, w kolejności macierzy). */
export function permissionsForRoles(roles: readonly Role[]): readonly Permission[] {
  const granted = new Set<Permission>()
  for (const role of roles) {
    for (const perm of ROLE_PERMISSIONS[role]) granted.add(perm)
  }
  return PERMISSIONS.filter((perm) => granted.has(perm))
}

/** Wymaganie guardu trasy/pola: jedno uprawnienie albo lista alternatyw (wystarczy dowolne). */
export type PermissionRequirement = Permission | readonly Permission[]

export function hasAnyPermission(
  roles: readonly Role[],
  requirement: PermissionRequirement,
): boolean {
  const alternatives = typeof requirement === 'string' ? [requirement] : requirement
  return alternatives.some((perm) => hasPermission(roles, perm))
}

/**
 * Kontrakt guardu Fastify (preHandler): wymagane uprawnienie + deklaratywny zakres
 * danych, który warstwa API zawęża po stwierdzeniu uprawnienia
 * (tenant — cały tenant, team — podwładni po manager_id, self — wyłącznie własne dane).
 */
export interface RbacGuardSpec {
  readonly requirement: PermissionRequirement
  readonly scope: 'tenant' | 'team' | 'self'
}

/**
 * Uprawnienia otwierające trasę modułu aplikacji HR (§5.3) — JEDNO źródło prawdy
 * dla nawigacji i guardu tras SPA, dla klikalności pulpitu i dla podpowiedzi
 * asystenta liczonych w API (Fala 4, grupy 63 i 160). Rozjechana kopia dawałaby
 * najgorszy możliwy wynik: pozycję w menu albo przycisk podpowiedzi prowadzący
 * na ekran odmowy. Trasy szczegółów dziedziczą po module.
 *
 * Listy odpowiadają guardom API — `/app/dashboard` wymaga tego samego, czego
 * wymaga `GET /dashboard/hr`, więc przełożony (sam zakres zespołu) nie dostaje
 * kafla, którego serwer i tak by mu nie policzył.
 */
export const APP_ROUTE_PERMISSIONS = {
  '/app/dashboard': ['Kadry : Podgląd', 'Płace : Podgląd'],
  '/app/employees': ['Kadry : Podgląd', 'Zespół : Podgląd'],
  '/app/requests': ['Wnioski : Decyzja'],
  '/app/documents': ['Dokumenty : Podgląd'],
  '/app/time': ['Czas pracy : Podgląd', 'Czas pracy : Akceptacja'],
  '/app/leaves': ['Urlopy : Podgląd', 'Urlopy : Akceptacja'],
  '/app/health': ['Badania : Podgląd'],
  '/app/payroll': ['Płace : Podgląd'],
  '/app/declarations': ['Płace : Podgląd', 'Automat : Deklaracje'],
  '/app/workflows': ['Konfiguracja : Zarządzanie'],
  '/app/development': ['Kadry : Podgląd'],
  '/app/reports': ['Raporty : Podgląd'],
  '/app/integrations': ['Konfiguracja : Zarządzanie'],
  '/app/settings': ['Konfiguracja : Zarządzanie'],
  '/app/audit': ['Audyt : Podgląd'],
} as const satisfies Record<string, readonly Permission[]>

/**
 * Czy rola otworzy trasę aplikacji HR — dopasowanie prefiksu modułu do
 * APP_ROUTE_PERMISSIONS (grupa 160). Pulpit robi z tego klikalność kafli
 * i pozycji „wymaga uwagi", a API filtr podpowiedzi asystenta: cel poza
 * zasięgiem roli nie jest przyciskiem ani podpowiedzią. Ścieżka spoza
 * modułów aplikacji HR daje false.
 */
export function canOpenAppPath(roles: readonly string[], path: string): boolean {
  const module = (Object.keys(APP_ROUTE_PERMISSIONS) as Array<keyof typeof APP_ROUTE_PERMISSIONS>).find(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  )
  if (module === undefined) return false
  const known = roles.filter(isRole)
  return APP_ROUTE_PERMISSIONS[module].some((permission) => hasPermission(known, permission))
}

/**
 * Pozycje „wymaga uwagi" pulpitu HR, które dotyczą modułu z własnym
 * uprawnieniem (grupa 160) — listy to uprawnienia trasy modułu, do którego
 * pozycja prowadzi, więc pulpit i asystent mówią to samo. API pomija je dla
 * roli bez żadnego z uprawnień: płace nie dostają pozycji oczekujących
 * wniosków ani wygasających badań. Kafel „Otwarte wnioski" zostaje w KPI,
 * ale bez przejścia do modułu, którego rola nie otworzy.
 */
export const DASHBOARD_ATTENTION_PERMISSIONS: Readonly<Record<string, readonly Permission[]>> = {
  pending_leaves: APP_ROUTE_PERMISSIONS['/app/leaves'],
  pending_requests: APP_ROUTE_PERMISSIONS['/app/requests'],
  expiring_health: APP_ROUTE_PERMISSIONS['/app/health'],
  /* „Następne kroki" pulpitu (fala 8, grupa 118) — ten sam filtr co „Co wymaga uwagi". */
  verify_payroll: APP_ROUTE_PERMISSIONS['/app/payroll'],
  send_declarations: APP_ROUTE_PERMISSIONS['/app/declarations'],
  prepare_declarations: APP_ROUTE_PERMISSIONS['/app/declarations'],
  decide_leaves: APP_ROUTE_PERMISSIONS['/app/leaves'],
  finish_onboarding: APP_ROUTE_PERMISSIONS['/app/employees'],
}

/**
 * Klucze i18n modułów i członów uprawnień w dzienniku audytu (fala 9, grupa 51).
 * Wartość w dzienniku zostaje polska — jest częścią skrótu wiersza (ADR-0003)
 * i wartością filtra — a ekran tłumaczy ją przy wyświetlaniu przez te mapy.
 */
export const AUDIT_MODULE_KEYS: Readonly<Record<string, string>> = {
  Audyt: 'audyt',
  Automat: 'automat',
  Badania: 'badania',
  'Czas pracy': 'czas_pracy',
  Dokumenty: 'dokumenty',
  Kadry: 'kadry',
  Konfiguracja: 'konfiguracja',
  Płace: 'place',
  Portal: 'portal',
  Raporty: 'raporty',
  Role: 'role',
  Tożsamość: 'tozsamosc',
  Urlopy: 'urlopy',
  Wnioski: 'wnioski',
  Zespół: 'zespol',
}

export const PERMISSION_ACTION_KEYS: Readonly<Record<string, string>> = {
  Podgląd: 'podglad',
  Edycja: 'edycja',
  'Dane wrażliwe': 'dane_wrazliwe',
  Zapraszanie: 'zapraszanie',
  Składanie: 'skladanie',
  Decyzja: 'decyzja',
  Wnioskowanie: 'wnioskowanie',
  Akceptacja: 'akceptacja',
  Rejestracja: 'rejestracja',
  'Zamykanie okresów': 'zamykanie_okresow',
  Zatwierdzanie: 'zatwierdzanie',
  Deklaracje: 'deklaracje',
  Zarządzanie: 'zarzadzanie',
  'Własne dane': 'wlasne_dane',
  Logowanie: 'logowanie',
}
